import '../css/main.scss'
import 'bootstrap'
import { constVoid, DOM, IOO, O, pipe, RA, RNEA, RR, s, v } from '@packages/prelude'
import { Offcanvas } from 'bootstrap'
import { Swiper } from 'swiper'
import { Autoplay, Navigation } from 'swiper/modules'
import { SwiperOptions } from 'swiper/types'

type OptionsFactory = (config: { sliderId: string }) => SwiperOptions

const getSliderId = (element: HTMLElement) => pipe(element.dataset.sliderId, O.fromNullable)

const setupProjectLinkHandler = (swiper: Swiper, sliderId: string) =>
  pipe(
    IOO.of({ document }),
    IOO.chain(({ document }) => pipe(document, DOM.querySelector(`.projects-slider-link[data-slider-id="${sliderId}"]`))),
    IOO.map(button => {
      button.addEventListener('click', () => {
        pipe(
          swiper.slides,
          RA.lookup(swiper.activeIndex),
          O.chain((slide: HTMLElement) => pipe(slide.dataset.projectUrl, O.fromNullable)),
          O.map(url => {
            window.location.href = url
          })
        )
      })
    })
  )()

const makeSwiper = (selector: string, createOptions: OptionsFactory) =>
  pipe(
    IOO.of({ document }),
    IOO.chain(
      ({ document }) =>
        pipe(document, DOM.querySelectorAll(`.${selector}`)) as unknown as IOO.IOOption<RNEA.ReadonlyNonEmptyArray<HTMLElement>>
    ),
    IOO.fold(
      () => constVoid,
      elements =>
        IOO.of(
          pipe(
            elements,
            RA.filterMap(element =>
              pipe(
                O.Do,
                O.apS('sliderId', getSliderId(element)),
                O.bind('options', ({ sliderId }) => O.of(createOptions({ sliderId }))),
                O.bind('swiper', ({ options }) => O.of(new Swiper(element, options))),
                O.chainFirst(({ swiper, sliderId }) => O.of(setupProjectLinkHandler(swiper, sliderId)))
              )
            )
          )
        )
    )
  )()

const Swipers: Record<string, OptionsFactory> = {
  'projects-slider': config => ({
    direction: 'horizontal',
    modules: [Autoplay, Navigation],
    loop: true,
    autoplay: {
      delay: 5000
    },
    slidesPerView: 1,
    slidesPerGroup: 1,
    spaceBetween: 24,
    navigation: {
      nextEl: `.swiper-button-next[data-slider-id="${config.sliderId}"]`,
      prevEl: `.swiper-button-prev[data-slider-id="${config.sliderId}"]`
    }
  })
}

pipe(
  Swipers,
  RR.foldMapWithIndex(s.Ord)(v.Monoid)((selector, optionsFactory) => makeSwiper(selector, optionsFactory))
)

// auto close offcanvas on click to jump links
// since bootstrap focuses the clicked element (menu button) after closing the offcanvas menu (aka 'Focus Restoration'),
// we need to scroll to the target element after the offcanvas is hidden
pipe(
  IOO.of({ document }),
  IOO.bind('offcanvasElement', ({ document }) => pipe(document, DOM.querySelector('#navbar-main.offcanvas'))),
  IOO.bind('offcanvas', ({ offcanvasElement }) => IOO.of(new Offcanvas(offcanvasElement))),
  IOO.bind('elements', ({ offcanvasElement }) =>
    pipe(offcanvasElement, DOM.querySelectorAll('a.nav-link:not(.dropdown-toggle), a.dropdown-item'))
  ),
  IOO.chain(({ offcanvas, offcanvasElement, elements }) =>
    pipe(
      elements,
      RA.map(element =>
        pipe(
          element,
          DOM.addEventListener('click')(e => () => {
            pipe(
              document,
              DOM.querySelector((e.target as HTMLAnchorElement).hash),
              IOO.fold(
                () => () => offcanvas.hide(),
                link => () => {
                  e.preventDefault()

                  offcanvasElement.addEventListener(
                    'hidden.bs.offcanvas',
                    () => {
                      link.scrollIntoView({ behavior: 'smooth' })
                    },
                    { once: true }
                  )
                  offcanvas.hide()
                }
              )
            )()
          })
        )
      ),
      IOO.traverseReadonlyArrayWithIndex((_, element) => IOO.fromIO(element))
    )
  )
)()
